import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { selectCompletableSessionSteps } from "@cliently/schemas";
import { api } from "../lib/api";
import { ALL_CLIENTS_KEY, useOrgStore } from "../stores/org.store";

type PortalExerciseInfo = {
  name: string;
  nameFr: string | null;
  images: string[];
  youtubeUrl: string | null;
  instructions: string[];
  instructionsFr: string[];
  primaryMuscles: string[];
  category: string | null;
  level: string | null;
  equipment: string | null;
};

export type PortalBlockMedia = {
  kind: "exercise" | "link";
  url: string;
  title: string | null;
  exerciseId: string | null;
  exercise: PortalExerciseInfo | null;
};

export type PortalProgramStep = {
  id: string;
  groupId: string | null;
  groupTitle: string | null;
  groupDescription: string | null;
  groupMediaUrl: string | null;
  title: string;
  description: string | null;
  type: "CHECKBOX" | "NUMBER" | "DURATION" | "TEXT";
  targetValue: number | null;
  targetUnit: string | null;
  position: number;
  isDone: boolean;
  valueText: string | null;
  valueNumber: number | null;
  exerciseId: string | null;
  exercise: PortalExerciseInfo | null;
  blockMedias?: PortalBlockMedia[];
};

type PortalProgramCompletionCycle = {
  id: string;
  cycleIndex: number;
  progressPercent: number;
  completedAt: string;
};

export type PortalProgramSessionStatus =
  | "PENDING"
  | "DONE"
  | "PARTIAL"
  | "MISSED";

export type PortalAssignmentSession = {
  id: string;
  cycleIndex: number;
  weekIndex: number;
  position: number;
  title: string;
  description: string | null;
  scheduledAt: string | null;
  completedAt: string | null;
  missedAt: string | null;
  status: PortalProgramSessionStatus;
  clientNote: string | null;
  coachNote: string | null;
  progressPercent: number;
  steps: PortalProgramStep[];
};

export type PortalProgramAssignment = {
  id: string;
  /** Toujours présent côté API ; nécessaire pour PATCH en mode agrégé (multi-cabinets). */
  clientId: string;
  assignedAt: string;
  visibleFrom: string | null;
  firstViewedAt: string | null;
  visibilityActivatedAt: string | null;
  visibilityExpiresAt: string | null;
  progress: number;
  completedCyclesCount: number;
  completionCycles: PortalProgramCompletionCycle[];
  steps: PortalProgramStep[];
  sessions: PortalAssignmentSession[];
  program: {
    id: string;
    title: string;
    description: string | null;
    visualStyle: "LOTTIE" | "LORDICON" | "UNDRAW";
    recurrenceIntervalWeeks: number | null;
    contentMode?: "IN_APP" | "PDF";
    pdfOriginalName?: string | null;
    org?: { id: string; name: string } | null;
  };
};



export const isStepCompleted = (step: PortalProgramStep): boolean => {
  if (step.type === "CHECKBOX") return step.isDone;
  if (step.type === "TEXT") return Boolean(step.valueText?.trim().length);
  if (step.type === "NUMBER" || step.type === "DURATION") {
    if (step.valueNumber === null) return false;
    if (step.targetValue === null || step.targetValue <= 0) return step.valueNumber > 0;
    return step.valueNumber >= step.targetValue;
  }
  return false;
}

const computeSessionProgress = (
  steps: PortalProgramStep[],
): { progressPercent: number; status: PortalProgramSessionStatus } => {
  const completable = selectCompletableSessionSteps(steps);
  if (completable.length === 0) {
    return { progressPercent: 0, status: "PENDING" };
  }
  const completed = completable.filter(isStepCompleted).length;
  const progressPercent = Math.round((completed / completable.length) * 100);
  const status: PortalProgramSessionStatus =
    progressPercent >= 100 ? "DONE" : progressPercent > 0 ? "PARTIAL" : "PENDING";
  return { progressPercent, status };
};

const computeAssignmentProgress = (sessions: PortalAssignmentSession[]): number => {
  if (sessions.length === 0) return 0;
  const total = sessions.reduce((sum, session) => {
    const { progressPercent } = computeSessionProgress(session.steps);
    return sum + progressPercent;
  }, 0);
  return Math.round(total / sessions.length);
};

export const usePortalPrograms = () => {
  const activeClientId = useOrgStore((s) => s.activeClientId);
  const isAllProsMode = activeClientId === ALL_CLIENTS_KEY;

  return useQuery({
    queryKey: ["portal-programs", activeClientId],
    queryFn: async () => {
      const { data } = await api.get(
        isAllProsMode ? "/portal/aggregate/programs" : "/portal/programs",
      );
      return data as PortalProgramAssignment[];
    },
  });
}



export const useUpdatePortalProgramProgress = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, progress }: { id: string; progress: number }) => {
      const { data } = await api.patch(`/portal/program-assignments/${id}/progress`, {
        progress,
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["portal-programs"] });
    },
  });
}



export const useUpdateAssignmentSessionNoteClient = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      sessionId,
      clientId,
      clientNote,
    }: {
      sessionId: string;
      clientId: string;
      clientNote: string | null;
    }) => {
      const { data } = await api.patch(
        `/portal/program-assignment-sessions/${sessionId}/note`,
        { clientNote },
        { headers: { "X-Portal-Client-Id": clientId } },
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["portal-programs"] });
    },
  });
}

export const useUpdateAssignmentSessionStatusClient = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      sessionId,
      clientId,
      status,
    }: {
      sessionId: string;
      clientId: string;
      status: PortalProgramSessionStatus;
    }) => {
      const { data } = await api.patch(
        `/portal/program-assignment-sessions/${sessionId}/status`,
        { status },
        { headers: { "X-Portal-Client-Id": clientId } },
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["portal-programs"] });
    },
  });
}

export const useUpdateProgramAssignmentStep = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
      assignmentClientId,
    }: {
      id: string;
      data: { isDone?: boolean; valueText?: string | null; valueNumber?: number | null };
      /** En mode « tous les clients », l’API doit cibler le bon client (header X-Portal-Client-Id). */
      assignmentClientId: string;
    }) => {
      const { data: res } = await api.patch(
        `/portal/program-assignment-steps/${id}`,
        data,
        {
          headers: { "X-Portal-Client-Id": assignmentClientId },
        },
      );
      return res;
    },
    onMutate: async (variables) => {
      await qc.cancelQueries({ queryKey: ["portal-programs"] });
      const previous = qc.getQueriesData<PortalProgramAssignment[]>({
        queryKey: ["portal-programs"],
      });

      qc.setQueriesData<PortalProgramAssignment[]>(
        { queryKey: ["portal-programs"] },
        (current) => {
          if (!current) return current;
          return current.map((assignment) => {
            const hasStep = assignment.steps.some((step) => step.id === variables.id);
            if (!hasStep) return assignment;
            const nextSteps = assignment.steps.map((step) =>
              step.id === variables.id ? { ...step, ...variables.data } : step,
            );
            const nextSessions = assignment.sessions.map((session) => {
              const sessionSteps = session.steps.map((step) =>
                step.id === variables.id ? { ...step, ...variables.data } : step,
              );
              const { progressPercent, status } = computeSessionProgress(sessionSteps);
              return {
                ...session,
                steps: sessionSteps,
                progressPercent,
                status,
                completedAt:
                  status === "DONE"
                    ? (session.completedAt ?? new Date().toISOString())
                    : null,
              };
            });
            return {
              ...assignment,
              steps: nextSteps,
              sessions: nextSessions,
              progress: computeAssignmentProgress(nextSessions),
            };
          });
        },
      );

      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (!context?.previous) return;
      context.previous.forEach(([queryKey, data]) => {
        qc.setQueryData(queryKey, data);
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["portal-programs"] });
    },
  });
}

